import { Divider, Radio, Typography } from "antd"
import { InvoicingSettings, InvoicingSystemSettings, QboAccountFieldErrors } from "../../../domain/invoicing/settings/invoicingSettings"
import VSpace from "../../common/VSpace"
import SettingsCard from "../SettingsCard"
import SetupSynkedUPPayments, { setupSynkedUPPaymentsHasContent } from "./SetupSynkedUPPayments"
import RadioButton from "../../common/RadioButton"
import { useEffect, useState } from "react"
import {
  areQboAccountsRequired,
  areQboAccountsValid,
  areSystemSettingsUpdated,
  getInvoicingSystemSettings,
  isSwitchingToQuickBooks,
} from "./invoicingSettingsModel"
import { SwitchToQuickBooksReason } from "./switchToQuickBooksFeedbackModel"
import { useUpdateInvoicingSystemSettingsMutation } from "../../../domain/invoicing/settings/invoicingSettingsQueries"
import { useQuickBooksConnectionStatus } from "../../SystemSetup/QuickBooks/quickBooksQueries"
import { useRainforestMerchantStatusQuery } from "../../../domain/invoicing/rainforestQueries"
import DefaultPaymentMethodsCard from "./DefaultPaymentMethodsCard"
import NextDayAchSettingsCard from "./NextDayAchSettingsCard"
import PaymentMethodOnFileSettingsCard from "./PaymentMethodOnFileSettingsCard"
import QboAccountsCard from "./QboAccountsCard"
import SwitchToQuickBooksFeedbackModal from "./SwitchToQuickBooksFeedbackModal"

type Props = {
  invoicingSettings: InvoicingSettings
}

const InvoicingSystemCard = ({ invoicingSettings }: Props) => {
  const [settings, setSettings] = useState<InvoicingSystemSettings>(getInvoicingSystemSettings(invoicingSettings))
  const [fieldErrors, setFieldErrors] = useState<QboAccountFieldErrors>({})
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false)
  const qboConnectionQuery = useQuickBooksConnectionStatus()
  const qboConnected = qboConnectionQuery.data === true
  const showQboAccounts = areQboAccountsRequired(settings, qboConnected)
  const isUpdateDisabled = !areSystemSettingsUpdated(settings, invoicingSettings) || !areQboAccountsValid(settings, qboConnected)
  const mutation = useUpdateInvoicingSystemSettingsMutation({ onFieldErrors: setFieldErrors })

  useEffect(() => {
    setSettings(getInvoicingSystemSettings(invoicingSettings))
    setFieldErrors({})
  }, [invoicingSettings])

  const updateSettings = (updates: Partial<InvoicingSystemSettings>) => {
    setSettings({ ...settings, ...updates })
  }

  const handleUpdate = () => {
    setFieldErrors({})
    if (isSwitchingToQuickBooks(settings, invoicingSettings)) {
      setIsFeedbackModalOpen(true)
      return
    }
    mutation.mutate(settings)
  }

  const handleSwitchConfirm = (reason: SwitchToQuickBooksReason, details?: string) => {
    window.posthog.capture('switched-to-quickbooks', { reason, details })
    mutation.mutate(settings, { onSettled: () => setIsFeedbackModalOpen(false) })
  }

  const isSynkedUP = settings.invoicingSystem === 'synkedUp'
  const merchantStatusQuery = useRainforestMerchantStatusQuery(isSynkedUP)
  const synkedUPHasContent = setupSynkedUPPaymentsHasContent(merchantStatusQuery.data, invoicingSettings.hideApprovedApplication)
  const showSynkedUPSettingsHeader = isSynkedUP && (showQboAccounts || synkedUPHasContent)

  return <SettingsCard
    title="Invoicing System"
    description="Choose how your organization will handle invoicing and payments."
    onUpdate={handleUpdate}
    isUpdating={mutation.isLoading}
    updateDisabled={isUpdateDisabled || mutation.isLoading}
  >
    <VSpace>
      <Radio.Group value={settings.invoicingSystem} style={{width: '100%'}} onChange={x => updateSettings({invoicingSystem: x.target.value})}>
        <VSpace>
          <RadioButton value={'synkedUp'}
            onBorderClick={() => updateSettings({invoicingSystem: 'synkedUp'})}
            checked={isSynkedUP}
          >
            <strong>SynkedUP</strong>
            <br/>
            <Typography.Text type="secondary">
              Accept payments directly in SynkedUP.
              Manage jobs and payments in one place&mdash;no external tools required.
            </Typography.Text>
          </RadioButton>
          <RadioButton value={'quickBooks'}
            onBorderClick={() => updateSettings({invoicingSystem: 'quickBooks'})}
            checked={settings.invoicingSystem === 'quickBooks'}
          >
            <strong>QuickBooks</strong>
            <br/>
            <Typography.Text type="secondary">
              Use your connected QuickBooks account to send and receive invoice payments.
              All transactions sync automatically with your accounting records.
            </Typography.Text>
          </RadioButton>
        </VSpace>
      </Radio.Group>
      {showSynkedUPSettingsHeader && <Divider plain>SynkedUP Settings</Divider>}
      {isSynkedUP && <SetupSynkedUPPayments hideApprovedApplication={invoicingSettings.hideApprovedApplication} />}
      {showQboAccounts && <QboAccountsCard
        qboDepositBankAccountId={settings.qboDepositBankAccountId}
        qboFeesExpenseAccountId={settings.qboFeesExpenseAccountId}
        fieldErrors={fieldErrors}
        onChange={updateSettings}
      />}
      <Divider plain>Payment Settings</Divider>
      <DefaultPaymentMethodsCard
        defaultPaymentMethods={settings.defaultPaymentMethods}
        onUpdate={defaultPaymentMethods => updateSettings({defaultPaymentMethods})}
      />
      {isSynkedUP && <PaymentMethodOnFileSettingsCard
        settings={settings.defaultAuthorizationForPaymentOnFile}
        onUpdate={defaultAuthorizationForPaymentOnFile => updateSettings({defaultAuthorizationForPaymentOnFile})}
      />}
      {isSynkedUP && <NextDayAchSettingsCard merchantStatusData={merchantStatusQuery.data} />}
    </VSpace>
    <SwitchToQuickBooksFeedbackModal
      isOpen={isFeedbackModalOpen}
      onConfirm={handleSwitchConfirm}
      onCancel={() => setIsFeedbackModalOpen(false)}
      isConfirming={mutation.isLoading}
    />
  </SettingsCard>
}

export default InvoicingSystemCard
